import { Table as TableType, Row, RowData } from '@tanstack/react-table';
import { createContext, useContext } from 'react';
import { TableFeatures, TableProps } from './Table';

export interface TableContextValue<T> extends TableFeatures<T> {
  table: TableType<T>;
  variant?: TableProps<T>['variant'];
  horizontalDensity?: TableProps<T>['horizontalDensity'];
}

// We use 'any' here because this context is meant to be generic and will be properly typed
// when consumed through the useTable<T> hook. The actual type safety is enforced at the usage site,
// not at the context creation.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableContext = createContext<TableContextValue<any>>(undefined!);

export function useTable<T extends RowData>() {
  const context = useContext<TableContextValue<T>>(TableContext);
  if (!context) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
}

export function createDummyRow<TData>(
  id: string = 'dummy',
  columnLength: number,
): Row<TData> {
  return {
    id,
    original: {} as TData,
    index: 0,
    depth: 0,
    // Required Row properties
    _getAllCellsByColumnId: () => ({}),
    _uniqueValuesCache: new Map(),
    _valuesCache: new Map(),
    getAllCells: () => [],
    getValue: () => null,
    getVisibleCells: () =>
      Array.from({ length: columnLength }).map((_, index) => ({
        column: {
          getIndex() {
            return index;
          },
        },
        row: {
          getVisibleCells() {
            return Array.from({ length: columnLength });
          },
          getIsExpanded() {
            return false;
          },
        },
      })),
    getIsSelected: () => false,
    getIsExpanded: () => false,
    getCanExpand: () => false,
    getCanSelect: () => false,
    toggleSelected: () => {},
    toggleExpanded: () => {},
    subRows: [],
    parentId: null,
    columnFilters: {},
    columnFiltersMeta: {},
  } as unknown as Row<TData>;
}
